import { ArrowLeft } from '@creditornot/cb-icons';
import { Spinner } from '@creditornot/cb-ingredients';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router';

import { APIErrorNotification } from 'common/components';
import { getOverrideSettings, updateOverrideSettings } from 'modules/shared-menu/api';
import { useScopeContext } from 'modules/shared-menu/contexts/ScopeContext';
import { OverrideSettingsResponse } from 'modules/shared-menu/types';

import { SettingRow } from './components/SettingRow';
import * as S from './components/SharedMenuSettingsPage.styles';

type SharedMenuSettingsPageProps = {
  rootUrl: string;
};

export type SettingsTab = keyof typeof settingsTabs;

export const settingsTabs = Object.freeze({
  VENUE_RESTRICTIONS: 'VENUE_RESTRICTIONS',
} as const);

type UpdateSettingsMutationProps = {
  settingToToggle: keyof Omit<OverrideSettingsResponse, 'scope'>;
  overrideSettings: OverrideSettingsResponse;
};

const settingToFieldNameMap: Record<UpdateSettingsMutationProps['settingToToggle'], string> = {
  venue_can_set_availability: 'Availability',
  venue_can_set_price: 'Price',
  venue_can_set_pos_id: 'POS ID',
  venue_can_set_merchant_sku: 'Merchant SKU',
};

export const SharedMenuSettingsPage = ({ rootUrl }: SharedMenuSettingsPageProps) => {
  const history = useHistory();
  const intl = useIntl();
  const scope = useScopeContext();

  const queryKey = ['shared-menu-settings', [scope.id]];
  const queryClient = useQueryClient();

  const [activeTabId, setActiveTabId] = useState(settingsTabs.VENUE_RESTRICTIONS);

  const goBackToSharedMenuRoot = useCallback(() => history.push(rootUrl), [rootUrl, history]);

  const {
    data: overrideSettings,
    isInitialLoading: initialLoadingOverrideSettings,
    error: errorWhileLoadingOverrideSettings,
    refetch: refetchOverrideSettings,
    isRefetching: refetchingOverrideSettings,
  } = useQuery(
    queryKey,
    () => {
      return getOverrideSettings(scope);
    },
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      staleTime: Infinity,
    },
  );

  const { mutate: executeUpdateOverrideSettings, isLoading: updatingOverrideSettings } = useMutation(
    (props: UpdateSettingsMutationProps) => {
      const payload: OverrideSettingsResponse = {
        ...props.overrideSettings,
        [props.settingToToggle]: !props.overrideSettings[props.settingToToggle],
      };

      // Optimistically update the local data to instantly toggle the switch position
      queryClient.setQueryData(queryKey, payload);
      return updateOverrideSettings(payload);
    },
    {
      onError: (_error, variables) => {
        // TODO: Finalise error handling with design
        toast.error(`Failed to update ${variables.settingToToggle}`, { duration: Infinity });
      },
      onSuccess: (_data, variables) => {
        // TODO: Finalise success handling with design
        const newValue = !variables.overrideSettings[variables.settingToToggle];

        toast.success(
          <span>
            {!!newValue ? 'Venues can set ' : 'Venues cannot set '}
            <b>{settingToFieldNameMap[variables.settingToToggle]}</b>
          </span>,
        );
      },
      // Refetch in the background to make sure the data is in sync with the real data
      // In case of errors while updating, this will revert the toggle back to the correct position
      onSettled: () => refetchOverrideSettings(),
    },
  );

  // Disable changing settings while a request is underway
  // Makes sure there are no conflicts and/or potential race conditions
  const disableToggling = useMemo(
    () => refetchingOverrideSettings || updatingOverrideSettings,
    [refetchingOverrideSettings, updatingOverrideSettings],
  );

  return (
    <S.Root>
      <S.HeaderContainer>
        <S.Header>
          <S.StyledTextButton variant="black" size="small" icon={<ArrowLeft />} onClick={goBackToSharedMenuRoot}>
            <FormattedMessage id="shared-menu.settings-page.header.back-button-label" />
          </S.StyledTextButton>
          <FormattedMessage id="listing-manager.settings.title" />
        </S.Header>
      </S.HeaderContainer>
      <S.StyledTabs
        activeTabId={activeTabId}
        items={[
          {
            id: settingsTabs.VENUE_RESTRICTIONS,
            label: intl.formatMessage({ id: 'shared-menu.settings-page.tabs.venue-restrictions' }),
          },
        ]}
        onChange={(id: SettingsTab) => setActiveTabId(id)}
      />
      <S.Content>
        {!!errorWhileLoadingOverrideSettings && (
          <APIErrorNotification style={{ maxWidth: '500px' }} error={errorWhileLoadingOverrideSettings} />
        )}
        {initialLoadingOverrideSettings && <Spinner size="large" />}
        {/* TODO: Add localisation when copy-texts are finalised in designs */}
        {overrideSettings && (
          <>
            <SettingRow
              title="Price"
              description="Allows venues to set their own pricing"
              disableToggling={disableToggling}
              checked={overrideSettings.venue_can_set_price}
              onToggle={() =>
                executeUpdateOverrideSettings({ settingToToggle: 'venue_can_set_price', overrideSettings })
              }
            />
            <SettingRow
              title="Availability"
              description="Allows venues to set when items items are available and visible to customers"
              disableToggling={disableToggling}
              checked={overrideSettings.venue_can_set_availability}
              onToggle={() =>
                executeUpdateOverrideSettings({ settingToToggle: 'venue_can_set_availability', overrideSettings })
              }
              hint="Available in the “Availability” tab"
            />
            <SettingRow
              title="POS ID"
              description="Allows venues to set their own POS IDs"
              disableToggling={disableToggling}
              checked={overrideSettings.venue_can_set_pos_id}
              onToggle={() =>
                executeUpdateOverrideSettings({ settingToToggle: 'venue_can_set_pos_id', overrideSettings })
              }
              hint="Available in the “Details” tab"
            />
            <SettingRow
              title="Merchant SKU"
              description="Allows venues to set their own Merchant SKUs"
              disableToggling={disableToggling}
              checked={overrideSettings.venue_can_set_merchant_sku}
              onToggle={() =>
                executeUpdateOverrideSettings({ settingToToggle: 'venue_can_set_merchant_sku', overrideSettings })
              }
              hint="Available in the “Details” tab"
            />
          </>
        )}
      </S.Content>
    </S.Root>
  );
};
